import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { Banner, Button, Field, Input, Modal, useToast } from "@/shared/rd";
import { useExtendStoreTrialMutation } from "../../services/mutations/useExtendStoreTrialMutation";

type Props = {
  storeId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

/** One question (how many days), one input, two buttons. */
export function StoreExtendTrialModal({
  storeId,
  isOpen,
  onOpenChange,
  onSuccess,
}: Props) {
  const t = useTranslations();
  const [days, setDays] = useState("7");
  const toast = useToast();
  const { mutateAsync, isPending, error, reset } = useExtendStoreTrialMutation();

  if (!isOpen) {
    return null;
  }

  const parsedDays = Number(days);
  const isValid = Number.isInteger(parsedDays) && parsedDays >= 1 && parsedDays <= 365;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || isPending) {
      return;
    }
    reset();
    try {
      await mutateAsync({ storeId, days: parsedDays });
    } catch {
      // Said in the banner at the top of the form (from `error`).
      return;
    }
    toast.show({ message: t("admin.trialExtendedToast", { days: parsedDays }) });
    onOpenChange(false);
    onSuccess?.();
  };

  const formId = "extend-trial-form";

  return (
    <Modal
      open
      onClose={() => onOpenChange(false)}
      title={t(keys.admin.storeSubscriptionsExtendTrialTitle)}
      actions={
        <>
          <Button variant="ghost" muted onClick={() => onOpenChange(false)}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={!isValid}
            loading={isPending}
            loadingLabel={t("admin.busyExtending")}
          >
            {t(keys.admin.storeSubscriptionsExtendTrialSubmit)}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error ? (
          <Banner tone="error" title={t("admin.trialExtendFailedTitle")}>
            {error instanceof Error && error.message ? error.message : t(keys.errors.generic)} {t("admin.nothingChanged")}
          </Banner>
        ) : null}
        <Field
          label={t(keys.admin.storeSubscriptionsExtendTrialDaysLabel)}
          htmlFor={`${formId}-days`}
          error={isValid ? undefined : t("admin.trialDaysInvalid")}
        >
          <Input
            id={`${formId}-days`}
            type="number"
            min={1}
            max={365}
            value={days}
            invalid={!isValid}
            onChange={(event) => setDays(event.target.value)}
            style={{ maxWidth: 160 }}
          />
        </Field>
      </form>
    </Modal>
  );
}
